import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import StickyMobileCTA from '@/components/StickyMobileCTA';
import SEOHead from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle, XCircle, Video, Film, Mic, Camera, Monitor, Users, Heart, Shield, Clock, Sparkles } from 'lucide-react';
import videographyImage from '@/assets/shooting-outdoor-event.jpg';
import studioImage from '@/assets/shooting-studio.jpg';
import teamImage from '@/assets/team-shooting-2.jpg';

const ServiceVideoProduction = () => {
  const painPoints = [
    { problem: "You know you need video but don't know where to start", solution: "We guide you through every step—from concept to final delivery—so you never feel lost" },
    { problem: "You've seen 'professional' videos that looked cheap and generic", solution: "Cinematic quality with a storytelling approach that makes your brand unforgettable" },
    { problem: "Past videographers delivered footage months late with endless excuses", solution: "Clear timelines, regular updates, and on-time delivery—every single time" },
    { problem: "You think professional video is only for big brands with big budgets", solution: "Premium production at accessible pricing—because every business deserves to look world-class" },
  ];

  const included = [
    { icon: Film, title: 'Pre-Production Planning', desc: 'Script development, storyboarding, location scouting, and shot lists—so nothing is left to chance.' },
    { icon: Camera, title: '4K Cinema Production', desc: 'Professional-grade cameras, lighting, and equipment that make your brand look like a million dollars.' },
    { icon: Mic, title: 'Professional Audio', desc: 'Crystal-clear sound with professional microphones and audio equipment—because bad audio ruins great video.' },
    { icon: Monitor, title: 'Expert Post-Production', desc: 'Color grading, motion graphics, sound design, and editing that turns raw footage into compelling stories.' },
    { icon: Video, title: 'Multi-Format Delivery', desc: 'Optimized versions for social media, website, ads, and presentations—every format you\'ll ever need.' },
    { icon: Sparkles, title: 'Brand Consistency', desc: 'Every frame reflects your brand identity, ensuring a cohesive look across all your content.' },
  ];

  const bigAgencyProblems = [
    'Massive production crews that inflate costs',
    'Weeks of back-and-forth with project managers',
    'Cookie-cutter corporate videos that bore everyone',
    'Your project gets handed off to junior editors',
    '$15K+ minimums for a single video',
    'They don\'t understand YOUR industry',
  ];

  const vpmDifference = [
    'Lean, skilled crew = lower costs, same quality',
    'Direct access to the people making your content',
    'Story-driven videos that captivate and convert',
    'Senior creatives on every project, start to finish',
    'Flexible packages that fit real business budgets',
    'Deep industry knowledge in med spas, dental, finance & more',
  ];

  const videoTypes = [
    'Brand Films & Stories',
    'Social Media Content',
    'Promotional Videos',
    'Event Coverage',
    'Testimonial Videos',
    'Product Showcases',
    'Training & Educational',
    'Real Estate Tours',
  ];

  const faqs = [
    { q: "How long does a typical project take?", a: "Most projects are delivered within 2-3 weeks from shoot day. We'll give you a clear timeline during our discovery call, and we stick to it. No ghosting, no delays." },
    { q: "What if I don't know what kind of video I need?", a: "That's literally what we're here for. During our free discovery call, we'll learn about your goals and recommend exactly what type of video will drive the best results for your business." },
    { q: "Do you travel for shoots?", a: "Absolutely. We're based in Central Ohio but regularly travel for projects. We'll handle all the logistics—you just show up and look great." },
    { q: "How many revisions do I get?", a: "We include revision rounds in every package. But honestly, most clients are thrilled with the first cut because we nail the vision in pre-production. We don't start filming until we're aligned." },
    { q: "Can I use the video on multiple platforms?", a: "100%. We deliver optimized versions for every platform—Instagram, YouTube, your website, ads, presentations. One shoot, unlimited possibilities." },
    { q: "What's the investment?", a: "Every project is different, but our packages are designed to give you big-agency quality at a fraction of the cost. Book a discovery call for a transparent, no-pressure quote." },
  ];

  return (
    <div className="min-h-screen bg-background pb-16 md:pb-0">
      <SEOHead
        title="Video Production Columbus Ohio | Cinematic Brand Videos | Vantage Point Media"
        description="Professional video production in Columbus Ohio. Cinematic brand films, promotional videos, social media content & event coverage. 4K quality, story-driven, delivered on time. Free discovery call available."
        canonical="https://vantagepointmediagroup.com/services/video-production"
        keywords="video production Columbus Ohio, videographer Columbus Ohio, brand video Columbus, promotional video Columbus Ohio, corporate video Columbus, event videography Columbus Ohio, 4K video production Ohio"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Video Production Columbus Ohio",
          "provider": { "@type": "LocalBusiness", "name": "Vantage Point Media", "url": "https://vantagepointmediagroup.com" },
          "areaServed": { "@type": "State", "name": "Ohio" },
          "description": "Professional cinematic video production for businesses in Columbus Ohio. Brand films, promotional videos, social media content, event coverage, and corporate videos. 4K quality with expert post-production.",
          "url": "https://vantagepointmediagroup.com/services/video-production"
        }}
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={videographyImage} alt="Video production" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/95 via-foreground/90 to-foreground/75" />
          </div>
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-secondary/20 backdrop-blur-sm border border-secondary/30 rounded-full px-4 py-1.5 mb-6">
                <span className="w-2 h-2 bg-secondary rounded-full animate-pulse" />
                <span className="text-background text-xs sm:text-sm font-medium">Video Production</span>
              </span>
              <h1 className="font-display font-extrabold text-3xl sm:text-5xl lg:text-6xl text-background leading-tight mb-6">
                Your Story Deserves
                <span className="block text-secondary mt-2">More Than a Smartphone.</span>
              </h1>
              <p className="text-base sm:text-xl text-background/85 max-w-2xl mb-8 leading-relaxed">
                In a world drowning in content, only the brands with cinematic, compelling video stand out. The rest get scrolled past. Which one will you be?
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="hero" size="xl" className="group" asChild>
                  <a href="/booking">
                    Book Your Free Discovery Call
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                We've Heard It All Before.
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg">
                Every concern you have? We've already solved it for dozens of businesses just like yours.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {painPoints.map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft">
                  <div className="flex items-start gap-3 mb-4">
                    <XCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-foreground/70 text-sm sm:text-base line-through decoration-destructive/40">{item.problem}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-foreground font-medium text-sm sm:text-base">{item.solution}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">Full-Service Production</span>
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                From Concept to Final Cut.
                <span className="text-primary block mt-1">We Handle Everything.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {included.map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-soft hover:shadow-medium transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-secondary" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Video Types */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8 sm:gap-12 items-center max-w-6xl mx-auto">
              <div className="w-full lg:w-1/2">
                <img src={studioImage} alt="Professional studio setup" className="w-full aspect-[4/3] object-cover rounded-2xl shadow-strong" />
              </div>
              <div className="w-full lg:w-1/2">
                <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">What We Create</span>
                <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground mb-6">
                  Every Type of Video Your Business Needs
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {videoTypes.map((type, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-foreground text-sm sm:text-base">{type}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Big Agency vs VPM */}
        <section className="py-16 sm:py-24 bg-foreground text-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
              <h2 className="font-display font-bold text-2xl sm:text-4xl mb-4">
                Big Production House vs. VPM
              </h2>
              <p className="text-background/70 text-base sm:text-lg">
                You shouldn't have to choose between quality and affordability.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
              <div className="bg-background/5 border border-background/10 rounded-2xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-destructive/20 rounded-xl flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-destructive" />
                  </div>
                  <h3 className="font-display font-bold text-xl">Big Production Houses</h3>
                </div>
                <ul className="space-y-4">
                  {bigAgencyProblems.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <XCircle className="w-4 h-4 text-destructive/70 flex-shrink-0 mt-1" />
                      <span className="text-background/70 text-sm sm:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-primary/10 border border-primary/20 rounded-2xl p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg">BETTER WAY</div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center">
                    <Heart className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-display font-bold text-xl">Vantage Point Media</h3>
                </div>
                <ul className="space-y-4">
                  {vpmDifference.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
                      <span className="text-background/90 text-sm sm:text-base">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Partnership */}
        <section className="py-16 sm:py-24 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row-reverse gap-8 sm:gap-12 items-center max-w-6xl mx-auto">
              <div className="w-full lg:w-1/2">
                <img src={teamImage} alt="VPM team on set" className="w-full aspect-[4/3] object-cover rounded-2xl shadow-strong" />
              </div>
              <div className="w-full lg:w-1/2">
                <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-3">The VPM Way</span>
                <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground mb-4">
                  We're Not Just Videographers.
                  <span className="text-primary block mt-1">We're Your Creative Partners.</span>
                </h2>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  We don't just show up, shoot, and disappear. We immerse ourselves in your brand, understand your audience, and create content that drives real business results—not just pretty footage that collects dust.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Users, text: 'We learn your business as if it were our own' },
                    { icon: Clock, text: 'On-time delivery, every time—no exceptions' },
                    { icon: Shield, text: 'Transparent pricing with no hidden fees' },
                    { icon: Heart, text: 'Your success is the only metric we care about' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-foreground text-sm sm:text-base">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="font-display font-bold text-2xl sm:text-4xl text-foreground mb-4">
                Still Have Questions?
              </h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="bg-card border border-border rounded-2xl shadow-soft group">
                  <summary className="flex items-center justify-between cursor-pointer p-5 sm:p-6 font-display font-semibold text-foreground text-sm sm:text-base">
                    {faq.q}
                    <span className="text-primary ml-4 group-open:rotate-45 transition-transform text-xl">+</span>
                  </summary>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground text-sm sm:text-base leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 sm:py-24 bg-foreground relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-secondary/10 to-transparent" />
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display font-bold text-2xl sm:text-4xl text-background mb-4">
              A Picture Is Worth 1,000 Words.
              <span className="text-secondary block mt-2">A Video? Priceless.</span>
            </h2>
            <p className="text-background/80 text-base sm:text-lg max-w-2xl mx-auto mb-8">
              Let's create something that makes your audience stop scrolling, start watching, and take action. It starts with a free discovery call.
            </p>
            <Button variant="hero" size="xl" className="group w-full sm:w-auto" asChild>
              <a href="/booking">
                Book Your Free Discovery Call
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
            <p className="mt-4 text-background/50 text-xs sm:text-sm">No contracts. No pressure. Just a conversation about your vision.</p>
          </div>
        </section>
      </main>

      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default ServiceVideoProduction;
